import React, { useEffect, useState, useCallback } from 'react';
import { WirelessAdvisorProvider, useWirelessAdvisor } from './context/WirelessAdvisorContext';
import SelectionModal from './SelectionModal';
import ResultsModal from './ResultsModal';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../../utils/analytics';

/**
 * WirelessAdvisorModalManager component
 * Manages the state and interaction between the modals
 * This is the main component that should be imported and used
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {boolean} props.startWithResults - Whether to start with the results modal
 */
const WirelessAdvisorModalManager = ({
  isOpen = false,
  onClose = () => {},
  startWithResults = false
}) => {
  const navigate = useNavigate();
  
  // State for modal visibility
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  
  // Initialize state based on props
  useEffect(() => {
    if (isOpen) {
      if (startWithResults) {
        // Start with the results modal if specified
        setIsResultsModalOpen(true);
      } else {
        // Otherwise start with the selection modal
        setIsSelectionModalOpen(true);
      }
      
      // Track modal open event
      trackEvent('wireless_advisor_opened', {
        start_with_results: startWithResults
      });
    } else {
      // Close all modals when isOpen is false
      setIsSelectionModalOpen(false);
      setIsResultsModalOpen(false);
    }
  }, [isOpen, startWithResults]);
  
  // Handle close
  const handleClose = () => {
    setIsSelectionModalOpen(false);
    setIsResultsModalOpen(false);
    
    // Track modal close event
    trackEvent('wireless_advisor_closed');
    
    onClose();
  };
  
  // Inner component to access context
  const ModalManager = () => {
    const { step } = useWirelessAdvisor();
    
    // Update modal visibility based on step
    useEffect(() => {
      if (step === 'results') {
        setIsSelectionModalOpen(false);
        setIsResultsModalOpen(true);
      }
    }, [step]);
    
    return (
      <>
        {/* Selection Modal */}
        <SelectionModal
          isOpen={isSelectionModalOpen}
          onClose={handleClose}
        />
        
        {/* Results Modal */}
        <ResultsModal
          isOpen={isResultsModalOpen}
          onClose={() => {
            setIsResultsModalOpen(false);
            // If we're closing the results modal, open the selection modal
            // unless we're closing everything
            if (isOpen) {
              setIsSelectionModalOpen(true);
            }
          }}
        />
      </>
    );
  };
  
  return (
    <WirelessAdvisorProvider
      initialState={{
        isSelectionModalOpen,
        isResultsModalOpen
      }}
    >
      <ModalManager />
    </WirelessAdvisorProvider>
  );
};

export default WirelessAdvisorModalManager;